import Controller from "../application_controller";

let turnstileLoaded;

const RESPONSE_FIELD_NAME = "cf-turnstile-response";

/**
 * Explicitly renders a Cloudflare Turnstile widget and removes it before Turbo
 * caches the page. By default the widget renders on connect.
 *
 * Set `data-cloudflare--turnstile-render-value="false"` when the widget starts
 * inside a hidden container, then call `cloudflare--turnstile#render` from an
 * event such as `appearance:visible`.
 */
export default class extends Controller {
  static values = {
    render: { type: Boolean, default: true },
    siteKey: String,
    theme: { type: String, default: "light" },
  };

  widgetId = null;
  widgetElement = null;

  connect() {
    if (this.element.closest("[data-turbo-preview]")) {
      return;
    }

    this.addEventListener(document.documentElement, "turbo:before-cache", (_) =>
      this.remove(),
    );

    // Hidden modals can keep generated tokens around long enough for them to
    // expire before use, so callers can opt into rendering on first appearance.
    if (this.renderValue) {
      this.render();
    }
  }

  disconnect() {
    super.disconnect();
    this.remove();
  }

  async render() {
    if (this.widgetId !== null) {
      return;
    }

    try {
      await loadTurnstile();
    } catch (error) {
      console.error(error);
      return;
    }

    if (!this.element.isConnected || this.widgetId !== null) {
      return;
    }

    this.widgetElement = document.createElement("div");
    this.element.append(this.widgetElement);

    try {
      this.widgetId = window.turnstile.render(
        this.widgetElement,
        this.turnstileOptions(),
      );
      this.element.dataset.cloudflareTurnstileWidgetId = this.widgetId;
    } catch (error) {
      console.error(error);
      this.remove();
    }
  }

  turnstileOptions() {
    const unavailable = () => this.remove();
    // Turnstile resets after expiry and timeout; only unavailable states remove it.
    const reset = () => {};

    return {
      sitekey: this.siteKeyValue,
      theme: this.themeValue,
      "error-callback": unavailable,
      "expired-callback": reset,
      "timeout-callback": reset,
      "unsupported-callback": unavailable,
    };
  }

  remove() {
    if (this.widgetId !== null && window.turnstile) {
      window.turnstile.remove(this.widgetId);
    }

    this.widgetId = null;
    this.widgetElement?.remove();
    this.widgetElement = null;
    delete this.element.dataset.cloudflareTurnstileWidgetId;
  }
}

function loadTurnstile() {
  if (window.turnstile) {
    return Promise.resolve();
  }

  turnstileLoaded ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return turnstileLoaded;
}
